import React from 'react';
import { IoIosLock } from 'react-icons/io';
import { AlertType } from '../../../../@types/enums';
import { ModuleMesh, RoofMesh } from '../../../../@types/types';
import { useViewerStore } from '../../../../store/store';
import * as THREE from 'three';
import { CheckSingleModuleRoofs } from '../Three-JS-Viewer/Controllers/ModuleControl';
import { checkIfAnyRoofHasModulesOverSetbackLines } from '../Three-JS-Viewer/Controllers/PathwayControl';
import CertificationValidation from '../Modal/Modal-Content/CertificationValidation';
import FinaliseConfirm from '../Modal/Modal-Content/FinaliseConfirm';
import LockedDesignValidation from '../Modal/Modal-Content/LockedDesignValidation';
import ModuleOverSetbackValidation from '../Modal/Modal-Content/ModuleOverSetbackValidation';
import RoofPitchValidation from '../Modal/Modal-Content/RoofPitchValidation';
import SingleModuleValidation from '../Modal/Modal-Content/SingleModuleValidation';
import TreePositionValidation from '../Modal/Modal-Content/TreePositionValidation';

type HasCapability = (name: string) => boolean;

/** Blocking reasons that would stop oneDRAW Finalize before Save Design opens. */
export function collectFinalizeGateIssues(hasCapability: HasCapability): string[] {
    const { sceneViewer, designList, isBallastedRackingMeeting25percentRule } =
        useViewerStore.getState();
    const issues: string[] = [];
    if (!sceneViewer) {
        issues.push('oneDRAW has not finished loading this site.');
        return issues;
    }

    if (
        designList[0]?.Design_Certification_Status &&
        designList[0]?.Design_Certification_Status !== 'Not Certified' &&
        designList[0]?.Design_Certification_Status !== null
    ) {
        const canOverride =
            hasCapability('Pre_Installed_Certified') ||
            hasCapability('Install_Change_Certified') ||
            hasCapability('As_Built_Certified') ||
            hasCapability('Certified_Design_Override');
        if (!canOverride) {
            issues.push(
                `This design is ${designList[0].Design_Certification_Status} and cannot be finalized.`,
            );
        }
    }

    const hasBallasted = sceneViewer.moduleMeshArr.filter(
        (module: ModuleMesh) => module.userData.isBallastedRackType,
    ).length > 0;
    if (hasBallasted && !isBallastedRackingMeeting25percentRule) {
        issues.push(
            'The design contains ballasted racking that does not meet the 25% rule.',
        );
    }

    const canOverrideSetbacks = useViewerStore.getState().isUserHaveCapabilityToAddPanelsPastSetbacks;
    if (!canOverrideSetbacks && checkIfAnyRoofHasModulesOverSetbackLines()) {
        issues.push('Modules are over setback lines.');
    }

    const financePartnerDetails = useViewerStore.getState().financePartnerDetails;
    if (financePartnerDetails?.name === 'Lightreach') {
        const invalidRoofs = sceneViewer.roofMeshArr.filter((roof: RoofMesh) => {
            const pitch = roof.oriAng.tilt;
            const hasModules = roof?.moduleArr?.length > 0;
            return hasModules && (pitch <= 5 || pitch > 45);
        });
        if (invalidRoofs.length > 0) {
            const roofIds = invalidRoofs.map((r: RoofMesh) => r.userData.roof_tag_id).join(', ');
            issues.push(
                `Lightreach only accepts roofs between 5° and 45°. Invalid: ${roofIds}.`,
            );
        }
    }

    const singleRoofs = CheckSingleModuleRoofs(sceneViewer.roofMeshArr);
    if (singleRoofs.length > 0) {
        issues.push('One or more roofs have a single-module array.');
    }

    return issues;
}

/** Same Save Design / validation sequence as the Actionbar Finalize button. */
export function startFinalizeSaveFlow(hasCapability: HasCapability): void {
    const { sceneViewer, designList, isBallastedRackingMeeting25percentRule } =
        useViewerStore.getState();
    if (!sceneViewer) return;

    const finaliseSave = () => {
        const doesDesignContainBallastedRacking = sceneViewer.moduleMeshArr.filter(
            (module: ModuleMesh) => module.userData.isBallastedRackType,
        ).length > 0;
        if (doesDesignContainBallastedRacking && !isBallastedRackingMeeting25percentRule) {
            sceneViewer.viewerAlert({
                show: true,
                title: '25% Rule Violation for Ballasted Racking',
                message:
                    'The design contains ballasted racking. Please ensure that the ballasted racking meets the 25% rule for the design to be finalized',
                messageType: AlertType.error,
                isModal: true,
            });
            return;
        }

        const isUserHaveSetbackOverrideCapability =
            useViewerStore.getState().isUserHaveCapabilityToAddPanelsPastSetbacks;
        const checkIfModulesAreOverSetback = checkIfAnyRoofHasModulesOverSetbackLines();

        if (!isUserHaveSetbackOverrideCapability && checkIfModulesAreOverSetback) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Error : Modules Over Setback Lines',
                message: <ModuleOverSetbackValidation />,
                messageType: AlertType.error,
                isModal: true,
            });
            return;
        }

        const financePartnerDetails = useViewerStore.getState().financePartnerDetails;

        if (financePartnerDetails?.name === 'Lightreach') {
            const invalidRoofs = sceneViewer.roofMeshArr.filter((roof: RoofMesh) => {
                const pitch = roof.oriAng.tilt;
                const hasModules = roof?.moduleArr?.length > 0;
                return hasModules && (pitch <= 5 || pitch > 45);
            });

            if (invalidRoofs.length > 0) {
                const roofIds = invalidRoofs.map((r: RoofMesh) => r.userData.roof_tag_id).join(', ');
                const roofIdString = invalidRoofs.length === 1 ? `Roof ${roofIds}` : `Roofs ${roofIds}`;
                sceneViewer.viewerAlert({
                    show: true,
                    title: 'Lightreach Design Validation',
                    message: `Cannot place modules on ${roofIdString}, because Lightreach will only accept designs on roofs between a 5° and 45° pitch`,
                    messageType: AlertType.error,
                    isModal: true,
                });
                return;
            }
        }

        const singleRoofs = CheckSingleModuleRoofs(sceneViewer.roofMeshArr);
        if (singleRoofs.length > 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Single Module Array',
                message: <SingleModuleValidation roofs={singleRoofs} />,
                messageType: AlertType.success,
                isModal: true,
            });
            return;
        }

        const roofsHavingModulesOnSteepRoof = sceneViewer.roofMeshArr.filter((roof: RoofMesh) => {
            return roof.oriAng.tilt > 50 && roof?.moduleArr?.length > 0;
        });

        const isTreeCollidingWithRoof = sceneViewer.treeGroup.children.filter((tree: THREE.Object3D) => {
            return tree.children[0].userData.isCollidingWithRoof;
        });

        if (roofsHavingModulesOnSteepRoof.length > 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Steep Roof Surface Design',
                message: <RoofPitchValidation roofs={roofsHavingModulesOnSteepRoof} />,
                messageType: AlertType.success,
                isModal: true,
            });
            return;
        }

        if (isTreeCollidingWithRoof.length > 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Tree Position Warning',
                message: <TreePositionValidation />,
                messageType: AlertType.warning,
                isModal: true,
            });
            return;
        }

        sceneViewer.viewerAlert({
            show: true,
            title: 'Save Design',
            message: <FinaliseConfirm />,
            messageType: AlertType.success,
            modalClassName: 'modal__content--save-design',
            isModal: true,
        });
    };

    if (
        designList[0]?.Design_Certification_Status &&
        designList[0]?.Design_Certification_Status !== 'Not Certified' &&
        designList[0]?.Design_Certification_Status !== null
    ) {
        if (
            hasCapability('Pre_Installed_Certified') ||
            hasCapability('Install_Change_Certified') ||
            hasCapability('As_Built_Certified') ||
            hasCapability('Certified_Design_Override')
        ) {
            if (designList[0]?.Design_Certification_Status !== 'Certified Design Override') {
                sceneViewer.viewerAlert({
                    show: true,
                    title: (
                        <span style={{ display: 'flex' }}>
                            <IoIosLock size={25} /> {designList[0]?.Design_Certification_Status}
                        </span>
                    ),
                    message: (
                        <CertificationValidation
                            confirm={finaliseSave}
                            certification_status={designList[0]?.Design_Certification_Status}
                        />
                    ),
                    messageType: AlertType.success,
                    isModal: true,
                });
            } else {
                finaliseSave();
            }
        } else {
            sceneViewer.viewerAlert({
                show: true,
                title: (
                    <span style={{ display: 'flex' }}>
                        <IoIosLock size={25} /> {designList[0]?.Design_Certification_Status}
                    </span>
                ),
                message: (
                    <LockedDesignValidation
                        certification_status={designList[0]?.Design_Certification_Status}
                    />
                ),
                messageType: AlertType.warning,
                isModal: true,
            });
        }
        return;
    }

    finaliseSave();
}
